import React, { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { ROOT_API } from "../../../api/config";
import "../subscriptions/style.scss";

// Internal admin-only tool (Croatian UI) — daily list of firms about to lose power, with a
// public email, so we can do B2B outreach. "Gotovo" marks a firm contacted -> it never shows
// again (so we don't email anyone twice), regardless of future outages.

const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("access_token")}`,
});

const normalizeUrl = (u) =>
  !u ? null : /^https?:\/\//i.test(u) ? u : `https://${u}`;

const fmtDate = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return d && m && y ? `${d}.${m}.${y}.` : iso;
};

// Flat one-line schedule for CSV: "23.06.2026. 08:00-14:00 | 24.06.2026. 09:00-13:00".
const slotsText = (l) => {
  const slots = l.outage_slots;
  if (!slots || slots.length === 0) return fmtDate(l.next_outage_date);
  return slots
    .map((s) => `${fmtDate(s.date)}${s.duration ? " " + s.duration : ""}`)
    .join(" | ");
};

const FRAMING_LABEL = { future: "najava", today: "danas", past: "bili bez struje" };

// Dry-run preview of what the automated 9am sender WOULD do today — the human approval step.
function SendPreview({ data, onClose }) {
  const s = data.stats || {};
  const live = data.send_enabled;
  const banner = !data.marketing_configured
    ? { bg: "#fdecea", fg: "#7a271a", text: "Marketing SMTP nije konfiguriran — slanje nije moguće (samo probni pregled)." }
    : live
    ? { bg: "#fff4e5", fg: "#7a4a12", text: "ŽIVO slanje je UKLJUČENO — sljedeći zakazani termin (≈9h) šalje stvarne e-poruke." }
    : { bg: "#e7f4ea", fg: "#1e4620", text: "Probni način — slanje je ISKLJUČENO. Ništa se ne šalje dok se ručno ne uključi." };
  const cell = { padding: "6px 10px", borderBottom: "1px solid #eee", fontSize: 13 };

  return (
    <div style={{ border: "1px solid #e7e0d4", borderRadius: 10, padding: 16, margin: "12px 0", background: "#fff" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <strong>Pregled slanja (probno — ništa se ne šalje)</strong>
        <button type="button" className="admin-subs__btn admin-subs__btn--small" onClick={onClose}>
          Zatvori
        </button>
      </div>

      <div style={{ background: banner.bg, color: banner.fg, padding: "8px 12px", borderRadius: 8, fontSize: 13, marginBottom: 10 }}>
        {banner.text}
      </div>

      <div style={{ fontSize: 13, color: "#5b6577", marginBottom: 10 }}>
        Razdoblje: <strong>{fmtDate(data.window?.from)}</strong> – <strong>{fmtDate(data.window?.to)}</strong>
        {"  ·  "}Kandidata: <strong>{s.candidates ?? 0}</strong>
        {"  ·  "}Za slanje: <strong>{data.would_send_total ?? 0}</strong>
        {data.would_send_total > (data.limit ?? 0) ? ` (danas najviše ${data.limit}, ostatak sutra)` : ""}
        <div style={{ marginTop: 4, fontStyle: "italic" }}>{data.note}</div>
      </div>

      <div style={{ fontSize: 13, marginBottom: 12 }}>
        Preskočeno (zaštita/usklađenost): nije ispravan email <strong>{s.invalid_email ?? 0}</strong>,
        odjavljeni <strong>{s.suppressed ?? 0}</strong>,
        osjetljive ustanove <strong>{s.sensitive ?? 0}</strong>,
        nedavno kontaktirani <strong>{s.recent ?? 0}</strong>,
        lanci/franšize <strong>{s.chain ?? 0}</strong>,
        zastarjelo (prošao termin) <strong>{s.stale ?? 0}</strong>.
      </div>

      <div style={{ fontWeight: 600, fontSize: 13, margin: "4px 0" }}>
        Poslalo bi se ({data.would_send?.length || 0}{data.would_send_total > (data.would_send?.length || 0) ? ` od ${data.would_send_total}` : ""}):
      </div>
      <div style={{ maxHeight: 280, overflow: "auto", border: "1px solid #eee", borderRadius: 8 }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={{ ...cell, textAlign: "left", color: "#8a6a12" }}>Naziv</th>
              <th style={{ ...cell, textAlign: "left", color: "#8a6a12" }}>Grad</th>
              <th style={{ ...cell, textAlign: "left", color: "#8a6a12" }}>Email</th>
              <th style={{ ...cell, textAlign: "left", color: "#8a6a12" }}>Termin</th>
            </tr>
          </thead>
          <tbody>
            {(data.would_send || []).map((r, i) => (
              <tr key={i}>
                <td style={cell}>{r.name}</td>
                <td style={cell}>{r.town}</td>
                <td style={cell}>{r.email}</td>
                <td style={cell}>{FRAMING_LABEL[r.framing] || r.framing} {fmtDate(r.outage_date)}</td>
              </tr>
            ))}
            {(data.would_send || []).length === 0 && (
              <tr><td style={cell} colSpan={4}>Nema firmi za slanje u ovom razdoblju.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {(data.skipped_examples || []).length > 0 && (
        <details style={{ marginTop: 10 }}>
          <summary style={{ cursor: "pointer", fontSize: 13, color: "#5b6577" }}>
            Primjeri preskočenih (osjetljivo / lanac / mrtva domena)
          </summary>
          <ul style={{ fontSize: 12, color: "#5b6577", margin: "6px 0 0", paddingLeft: 18 }}>
            {data.skipped_examples.map((x, i) => <li key={i}>{x}</li>)}
          </ul>
        </details>
      )}
    </div>
  );
}

const fmtWhen = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? iso
    : d.toLocaleString("hr-HR", {
        day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
      });
};

// Audit report of ACTUAL sends (the marketing_sends log) — who we emailed, when, status. Empty
// until the live sender has run (test/dry-run sends are deliberately NOT logged here).
function SendsReport({ data, onClose }) {
  const t = data.totals || {};
  const recent = data.recent || [];
  const daily = data.daily || [];
  const cell = { padding: "6px 10px", borderBottom: "1px solid #eee", fontSize: 13 };
  const stat = (label, val, hint) => (
    <div
      title={hint || ""}
      style={{ background: "#faf6ef", border: "1px solid #e7e0d4", borderRadius: 8, padding: "8px 12px", minWidth: 92 }}
    >
      <div style={{ fontSize: 20, fontWeight: 700, color: "#1c2231" }}>{val ?? 0}</div>
      <div style={{ fontSize: 11, color: "#5b6577" }}>{label}</div>
    </div>
  );

  const downloadCsv = () => {
    const cols = [
      ["Email", (r) => r.email],
      ["Grad", (r) => r.town],
      ["Nestanak", (r) => fmtDate(r.outage_date)],
      ["Tip", (r) => FRAMING_LABEL[r.framing] || r.framing],
      ["Status", (r) => r.status],
      ["Naslov", (r) => r.subject],
      ["Poslano", (r) => fmtWhen(r.sent_at)],
      ["Detalj", (r) => r.detail],
    ];
    const esc = (v) => {
      const s = v == null ? "" : String(v);
      return /[";\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [cols.map((c) => c[0]).join(";")];
    recent.forEach((r) => lines.push(cols.map((c) => esc(c[1](r))).join(";")));
    const blob = new Blob(["﻿" + lines.join("\r\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `nemastruje-poslane-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{ border: "1px solid #e7e0d4", borderRadius: 10, padding: 16, margin: "12px 0", background: "#fff" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <strong>Poslane e-poruke (stvarna povijest slanja)</strong>
        <div style={{ display: "flex", gap: 8 }}>
          <button type="button" className="admin-subs__btn admin-subs__btn--small" disabled={recent.length === 0} onClick={downloadCsv}>
            Preuzmi CSV ({recent.length})
          </button>
          <button type="button" className="admin-subs__btn admin-subs__btn--small" onClick={onClose}>
            Zatvori
          </button>
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
        {stat("ukupno poslano", t.sent)}
        {stat("danas", t.today)}
        {stat("7 dana", t.last7)}
        {stat("30 dana", t.last30)}
        {stat("primatelja", t.unique_recipients, "Različitih e-adresa")}
        {stat("neuspjelo", t.failed)}
      </div>

      {daily.length > 0 && (
        <div style={{ fontSize: 12, color: "#5b6577", marginBottom: 12 }}>
          Zadnjih 14 dana:{"  "}
          {daily.map((d) => `${fmtDate(d.date)} (${d.count})`).join("  ·  ")}
        </div>
      )}

      <div style={{ fontWeight: 600, fontSize: 13, margin: "4px 0" }}>
        Zadnje poslano ({recent.length}
        {data.limit && recent.length >= data.limit ? `, prikazano zadnjih ${data.limit}` : ""}):
      </div>
      <div style={{ maxHeight: 320, overflow: "auto", border: "1px solid #eee", borderRadius: 8 }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={{ ...cell, textAlign: "left", color: "#8a6a12" }}>Email</th>
              <th style={{ ...cell, textAlign: "left", color: "#8a6a12" }}>Grad</th>
              <th style={{ ...cell, textAlign: "left", color: "#8a6a12" }}>Termin</th>
              <th style={{ ...cell, textAlign: "left", color: "#8a6a12" }}>Status</th>
              <th style={{ ...cell, textAlign: "left", color: "#8a6a12" }}>Poslano</th>
            </tr>
          </thead>
          <tbody>
            {recent.map((r, i) => (
              <tr key={i}>
                <td style={cell}>{r.email}</td>
                <td style={cell}>{r.town || "—"}</td>
                <td style={cell}>
                  {(FRAMING_LABEL[r.framing] || r.framing || "—")} {fmtDate(r.outage_date)}
                </td>
                <td
                  style={{ ...cell, color: r.status === "sent" ? "#1e4620" : "#7a271a", fontWeight: 600 }}
                  title={r.detail || ""}
                >
                  {r.status === "sent" ? "poslano" : "neuspjelo"}
                </td>
                <td style={cell}>{fmtWhen(r.sent_at)}</td>
              </tr>
            ))}
            {recent.length === 0 && (
              <tr>
                <td style={cell} colSpan={5}>
                  Još nije poslana nijedna automatska e-poruka.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function MarketingLeads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [query, setQuery] = useState("");
  const [preview, setPreview] = useState(null); // dry-run send preview, or null when closed
  const [previewing, setPreviewing] = useState(false);
  const [status, setStatus] = useState(null); // {send_enabled, marketing_configured, send_hour, ...}
  const [sends, setSends] = useState(null); // actual-sends report, or null when closed
  const [sendsLoading, setSendsLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${ROOT_API}apis/admin/marketing-leads`, {
        headers: authHeaders(),
      });
      setLeads(res?.data?.data || []);
    } catch (e) {
      setLeads([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    axios
      .get(`${ROOT_API}apis/admin/marketing-status`, { headers: authHeaders() })
      .then((res) => setStatus(res?.data?.data || null))
      .catch(() => setStatus(null));
  }, []);

  const markDone = async (id) => {
    try {
      setBusy(id);
      await axios.post(
        `${ROOT_API}apis/admin/marketing-leads/done`,
        { id },
        { headers: authHeaders() }
      );
      // Optimistic: drop it from the visible list immediately.
      setLeads((prev) => prev.filter((l) => l.id !== id));
    } catch (e) {
      Swal.fire({ icon: "error", title: "Greška", text: "Nije uspjelo. Pokušajte ponovno." });
    } finally {
      setBusy(null);
    }
  };

  const loadPreview = async () => {
    try {
      setPreviewing(true);
      const res = await axios.get(`${ROOT_API}apis/admin/marketing-preview`, {
        headers: authHeaders(),
      });
      setPreview(res?.data?.data || null);
    } catch (e) {
      Swal.fire({ icon: "error", title: "Greška", text: "Pregled nije uspio." });
    } finally {
      setPreviewing(false);
    }
  };

  const loadSends = async () => {
    try {
      setSendsLoading(true);
      const res = await axios.get(`${ROOT_API}apis/admin/marketing-sends`, {
        headers: authHeaders(),
      });
      setSends(res?.data?.data || null);
    } catch (e) {
      Swal.fire({ icon: "error", title: "Greška", text: "Izvještaj o slanju nije uspio." });
    } finally {
      setSendsLoading(false);
    }
  };

  const refresh = async () => {
    try {
      setRefreshing(true);
      await axios.get(`${ROOT_API}apis/admin/marketing-refresh`, {
        headers: authHeaders(),
      });
      Swal.fire({
        icon: "info",
        title: "Osvježavanje pokrenuto",
        text: "Popis se gradi u pozadini (može potrajati minutu-dvije). Kliknite 'Učitaj ponovno' nakon toga.",
      });
    } catch (e) {
      Swal.fire({ icon: "error", title: "Greška", text: "Osvježavanje nije pokrenuto." });
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return <div className="admin-subs__state">Učitavanje…</div>;
  }

  const q = query.trim().toLowerCase();
  const rows = q
    ? leads.filter(
        (l) =>
          (l.name || "").toLowerCase().includes(q) ||
          (l.town || "").toLowerCase().includes(q) ||
          (l.email || "").toLowerCase().includes(q)
      )
    : leads;

  const downloadCsv = () => {
    const cols = [
      ["Naziv", (l) => l.name],
      ["Grad", (l) => l.town],
      ["Prvi nestanak", (l) => fmtDate(l.next_outage_date)],
      ["Termini", (l) => slotsText(l)],
      ["Email", (l) => l.email],
      ["Web", (l) => l.website],
      ["Telefon", (l) => l.phone],
      ["Kategorija", (l) => l.category],
      ["Odjava (link)", (l) => l.unsubscribe_url],
    ];
    // Escape for a semicolon-delimited CSV (HR Excel default); double internal quotes.
    const esc = (v) => {
      const s = v == null ? "" : String(v);
      return /[";\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [cols.map((c) => c[0]).join(";")];
    rows.forEach((l) => lines.push(cols.map((c) => esc(c[1](l))).join(";")));
    // Leading BOM so Excel reads UTF-8 (preserves č/ž/š).
    const blob = new Blob(["﻿" + lines.join("\r\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `nemastruje-marketing-leads-${new Date()
      .toISOString()
      .slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="admin-subs">
      <div className="admin-bills__summary" style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <span>
          Firme uskoro bez struje (s javnim emailom): <strong>{leads.length}</strong>
        </span>
        {status && (
          <span
            title={
              status.marketing_configured
                ? `Automatsko slanje u ${status.send_hour}h, najviše ${status.max_per_run}/dan, horizont ${status.horizon_days} dana`
                : "Marketing SMTP nije konfiguriran"
            }
            style={{
              fontSize: 12,
              fontWeight: 700,
              padding: "3px 10px",
              borderRadius: 999,
              background: status.send_enabled ? "#fff4e5" : "#eef0f3",
              color: status.send_enabled ? "#7a4a12" : "#5b6577",
              border: `1px solid ${status.send_enabled ? "#f0c98a" : "#d7dbe0"}`,
            }}
          >
            {status.send_enabled
              ? `● Slanje UKLJUČENO (${status.send_hour}h)`
              : status.marketing_configured
              ? "○ Slanje isključeno"
              : "○ Slanje isključeno (SMTP nije postavljen)"}
          </span>
        )}
        <button
          type="button"
          className="admin-subs__btn admin-subs__btn--small"
          disabled={refreshing}
          onClick={refresh}
        >
          {refreshing ? "Pokrećem…" : "Osvježi popis"}
        </button>
        <button
          type="button"
          className="admin-subs__btn admin-subs__btn--small"
          onClick={load}
        >
          Učitaj ponovno
        </button>
        <button
          type="button"
          className="admin-subs__btn admin-subs__btn--small"
          disabled={rows.length === 0}
          onClick={downloadCsv}
        >
          Preuzmi CSV ({rows.length})
        </button>
        <button
          type="button"
          className="admin-subs__btn admin-subs__btn--small"
          disabled={previewing}
          onClick={loadPreview}
          title="Što bi automatski sender poslao danas (probno, ništa se ne šalje)"
        >
          {previewing ? "Računam…" : "Pregled slanja"}
        </button>
        <button
          type="button"
          className="admin-subs__btn admin-subs__btn--small"
          disabled={sendsLoading}
          onClick={loadSends}
          title="Stvarna povijest poslanih e-poruka (tko, kada, status)"
        >
          {sendsLoading ? "Učitavam…" : "Poslane e-poruke"}
        </button>
      </div>

      {preview && (
        <SendPreview data={preview} onClose={() => setPreview(null)} />
      )}

      {sends && <SendsReport data={sends} onClose={() => setSends(null)} />}

      <input
        className="admin-subs__search"
        placeholder="Traži po nazivu, gradu ili emailu…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />

      {rows.length === 0 ? (
        <div className="admin-subs__state">
          Nema firmi za prikaz. Kliknite "Osvježi popis" da se izgradi iz nadolazećih nestanaka.
        </div>
      ) : (
        <table className="admin-subs__table">
          <thead>
            <tr>
              <th>Firma</th>
              <th>Grad</th>
              <th>Nestanak</th>
              <th>Email</th>
              <th>Web</th>
              <th>Telefon</th>
              <th>Akcija</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((l) => {
              const web = normalizeUrl(l.website);
              const slots = l.outage_slots || [];
              return (
                <tr key={l.id}>
                  <td>
                    <div className="admin-subs__name">{l.name}</div>
                    {l.category ? (
                      <div className="admin-subs__email">{l.category}</div>
                    ) : null}
                  </td>
                  <td>{l.town || "—"}</td>
                  <td>
                    {slots.length === 0 ? (
                      fmtDate(l.next_outage_date)
                    ) : slots.length === 1 ? (
                      <span>
                        {fmtDate(slots[0].date)}
                        {slots[0].duration ? `, ${slots[0].duration}` : ""}
                      </span>
                    ) : (
                      <div>
                        <div>
                          <strong>Više dana ({slots.length})</strong>
                        </div>
                        {slots.map((s, i) => (
                          <div key={i} className="admin-subs__email">
                            {fmtDate(s.date)}
                            {s.duration ? ` · ${s.duration}` : ""}
                          </div>
                        ))}
                      </div>
                    )}
                  </td>
                  <td>
                    {l.email ? (
                      <a href={`mailto:${l.email}`}>{l.email}</a>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td>
                    {web ? (
                      <a href={web} target="_blank" rel="noreferrer">
                        otvori
                      </a>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="admin-subs__email">{l.phone || "—"}</td>
                  <td>
                    <button
                      type="button"
                      className="admin-subs__btn admin-subs__btn--small"
                      disabled={busy === l.id}
                      onClick={() => markDone(l.id)}
                    >
                      {busy === l.id ? "…" : "Gotovo"}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
